/**
 * CTF MATCH TIMER
 *
 * Shows how long the current CTF match has been running. The clock is anchored
 * to a wall-clock timestamp in the local `Date.now()` domain and every render
 * recomputes `Date.now() - matchStartMs`, so timer skew and tab throttling
 * cannot accumulate error.
 *
 * Sources of the anchor:
 *   1. The `"Game starting!"` server message: `Date.now()` at receipt. ab-server
 *      records its own match start in the tick that broadcasts the message, so
 *      this anchor is exact.
 *   2. Joining a CTF server mid-match: the `/match` server command, answered by
 *      a `"Match time: 5m 23s."` whisper from the Server bot. The whisper is
 *      absorbed before chat sees it.
 *
 * The HUD stays hidden until the anchor is known, so the player never sees
 * 0:00 snap to the real time. If no reply arrives in time the clock anchors at
 * "now" so it at least appears; a reply that turns up later still corrects it,
 * and the next "Game starting!" corrects it for free.
 *
 * Between matches our server answers `/match` that no match is on, and the
 * clock stays hidden until "Game starting!". ab-server answers with the time
 * since the last match began even then, and nothing it sends tells a late
 * joiner that the match is over, so on its servers they see that clock ticking
 * until "Game starting!" resets it.
 *
 * Design notes vs SWAM's `getMatchTimer` (engine-swam-only.js, see the
 * [HARVESTED] marker):
 *   - SWAM relies on a separate `STATSBOT` bot replying to `-api-game-start`
 *     with a unix-ms timestamp. We use the native `/match` server command
 *     (ab-server `match.ts`), which works on every official server.
 *   - SWAM accumulates ticks; we re-read the wall clock each tick (drift-immune).
 *   - SWAM hides the chat line via DOM removal; we absorb the whisper at the
 *     network layer before chat ingest.
 */
import type { ServerChatWhisperMessage, ServerServerMessageMessage } from '@airmash/protocol';
import { GAME_TYPES, SERVER_MESSAGE_TYPES } from '@airmash/protocol';
import { sendCommand } from './Network';
import { get as playersGet } from './Players';
import { session } from './Session';
import { uiState } from '../ui/UIState';

const MS_PER_SECOND = 1000;
/**
 * How long to wait for the `/match` reply before anchoring at "now" so the clock
 * at least appears (a server without the command, packet loss). Do not lower
 * it: ab-server holds a new connection's first Server whisper until the socket
 * is two seconds old (its CHAT_FIRST_MESSAGE_SAFE_DELAY_MS), and this wait only
 * starts after the login round trip, so a real reply lands inside it.
 */
const MATCH_QUERY_TIMEOUT_MS = 3000;
/**
 * How long after sending `/match` its reply is still accepted. Once the fallback
 * has anchored "now", a late reply re-anchors the clock: one visible jump beats
 * a clock that is wrong for the rest of the match. Later still the whisper is
 * not ours to judge and reaches chat.
 */
const MATCH_REPLY_WINDOW_MS = 10_000;
/**
 * The `/match` reply. ab-server's msToHumanReadable lists the non-zero day,
 * hour, minute and second units separated by spaces, floored to the second,
 * or "0s"; the group captures that list.
 */
const MATCH_REPLY_RE = /^Match time: (\d+[dhms](?: \d+[dhms])*)\.$/;
/** Our server's `/match` reply between matches, when there is nothing to count. */
const NO_MATCH_REPLY = 'No match is on. The next one starts within a minute.';
const ELAPSED_UNIT_RE = /(\d+)([dhms])/g;
/** Text of the info message the server broadcasts the moment a CTF match begins. */
const MATCH_START_TEXT = 'Game starting!';

/** Match start in `Date.now()` terms; null until the first anchor. */
let matchStartMs: number | null = null;
/** The pending render, aimed just past the next whole second; null while the clock is hidden or frozen. */
let tickId: number | null = null;
/** The pending "no reply" fallback; null once it has fired or a reply has anchored the clock. */
let fallbackId: number | null = null;
/** `Date.now()` deadline until which a `/match` reply anchors the clock and is kept out of chat. */
let acceptReplyUntil = 0;

function formatHMS(ms: number): string {
  const totalSeconds = Math.floor(Math.max(0, ms) / MS_PER_SECOND);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Shows the time elapsed since `startMs` and returns it. */
function render(startMs: number): number {
  const elapsed = Date.now() - startMs;
  uiState.ctfMatchTimer = formatHMS(elapsed);
  return elapsed;
}

function stopTicking(): void {
  if (tickId !== null) {
    clearTimeout(tickId);
    tickId = null;
  }
}

function clearFallback(): void {
  if (fallbackId !== null) {
    clearTimeout(fallbackId);
    fallbackId = null;
  }
}

/**
 * Renders now and again just after the match's next whole second, so every
 * second shows exactly once. A free-running interval would sit at a random
 * phase and, when a tick slipped past a boundary, hold one value for two
 * seconds after showing the one before it for a flash.
 */
function tick(startMs: number): void {
  const elapsed = render(startMs);
  tickId = window.setTimeout(() => tick(startMs), MS_PER_SECOND - (elapsed % MS_PER_SECOND));
}

/** Anchors the clock at `startMs` and starts it ticking from there. */
function anchor(startMs: number): void {
  matchStartMs = startMs;
  clearFallback();
  stopTicking();
  tick(startMs);
}

/**
 * Hidden tabs defer timers, so a return to the tab re-syncs the tick phase.
 * Nothing to do while the clock is hidden or frozen.
 */
function onVisibilityChange(): void {
  if (!document.hidden && tickId !== null && matchStartMs !== null) {
    stopTicking();
    tick(matchStartMs);
  }
}

/** Parses the reply's elapsed part, e.g. "1h 2m 3s", into milliseconds. */
function parseElapsed(text: string): number {
  let ms = 0;
  for (const [, count, unit] of text.matchAll(ELAPSED_UNIT_RE)) {
    const n = Number(count);
    switch (unit) {
      case 'd':
        ms += n * 86_400_000;
        break;
      case 'h':
        ms += n * 3_600_000;
        break;
      case 'm':
        ms += n * 60_000;
        break;
      case 's':
        ms += n * MS_PER_SECOND;
        break;
    }
  }
  return ms;
}

/**
 * Intercepts the Server bot's `/match` reply. Returns true when the whisper was
 * absorbed, so the caller keeps it out of chat.
 */
export function matchTimerHandleWhisper(msg: ServerChatWhisperMessage): boolean {
  if (msg.to !== session.myID || Date.now() > acceptReplyUntil) return false;
  // Only the Server bot may anchor us; a player whispering "Match time: 1s." must not.
  if (playersGet(msg.from)?.bot !== true) return false;
  if (msg.text === NO_MATCH_REPLY) {
    // Nothing to count until "Game starting!": no fallback clock, and a late reply hides the one it showed.
    acceptReplyUntil = 0;
    clearFallback();
    stopTicking();
    matchStartMs = null;
    uiState.ctfMatchTimer = null;
    return true;
  }
  const reply = MATCH_REPLY_RE.exec(msg.text);
  if (reply === null) return false;
  acceptReplyUntil = 0;
  anchor(Date.now() - parseElapsed(reply[1]!));
  return true;
}

/**
 * Whether a server message is the CTF match-start broadcast. The countdown
 * before it arrives as alerts, so the type check keeps those out.
 */
export function isCtfMatchStart(msg: Pick<ServerServerMessageMessage, 'type' | 'text'>): boolean {
  return msg.type === SERVER_MESSAGE_TYPES.INFO && msg.text === MATCH_START_TEXT;
}

/** Called from the `"Game starting!"` server message handler: the exact anchor. */
export function notifyCtfMatchStarted(): void {
  if (session.gameType !== GAME_TYPES.CTF) return;
  // A `/match` reply still in flight could only be noise next to this anchor.
  acceptReplyUntil = 0;
  anchor(Date.now());
}

/**
 * Called from `showCTFWin` when a team wins. Freezes the display on the final
 * match length until the next `"Game starting!"` arrives.
 */
export function notifyCtfMatchEnded(): void {
  // A last render, or the frozen value could be up to a second stale.
  if (matchStartMs !== null) render(matchStartMs);
  stopTicking();
  clearFallback();
  acceptReplyUntil = 0;
}

/**
 * Starts the clock for the current CTF game. Called once per login from
 * `prep()`, after the game is PLAYING (sendCommand drops commands before that)
 * and always after `stopCtfMatchTimer` has cleared the previous game's state.
 * Asks the server for the elapsed time; the HUD stays hidden until the reply
 * or the fallback anchors the clock.
 */
export function startCtfMatchTimer(): void {
  if (session.gameType !== GAME_TYPES.CTF) return;
  document.addEventListener('visibilitychange', onVisibilityChange);
  acceptReplyUntil = Date.now() + MATCH_REPLY_WINDOW_MS;
  sendCommand('match', '');
  fallbackId = window.setTimeout(() => {
    fallbackId = null;
    anchor(Date.now());
  }, MATCH_QUERY_TIMEOUT_MS);
}

/** Stops the clock and clears all state. Called from reconnection cleanup. */
export function stopCtfMatchTimer(): void {
  document.removeEventListener('visibilitychange', onVisibilityChange);
  stopTicking();
  clearFallback();
  acceptReplyUntil = 0;
  matchStartMs = null;
  uiState.ctfMatchTimer = null;
}
